import { useEffect, useState } from "react";
import { OOSI_API_URI, HEADER_KEYS, EVENTS } from "../constants";
import { determineColorBasedOnTime } from "../utils/timeUtils";

const PREFIX = {
  access: "access-event-report/data/",
  probing: "probing-event-report/data/"
};

const OOSI_API_BASE = (() => {
  if (typeof window === "undefined") return "https://platformapi-stage.spacemap42.com";
  const hostname = window.location?.hostname;
  if (hostname === "localhost" || hostname === "127.0.0.1") {
    return "http://localhost:8084";
  }
  if (hostname === "oosi.spacemap42.com") {
    return "https://platformapi.spacemap42.com";
  }
  if (typeof OOSI_API_URI !== "undefined" && OOSI_API_URI) {
    return OOSI_API_URI.replace(/\/$/, "");
  }
  return "https://platformapi-stage.spacemap42.com";
})();

const fetchOosiData = async (prefix) => {
  const url = `${OOSI_API_BASE}/oosi/s3/data?prefix=${encodeURIComponent(prefix)}`;
  const res = await fetch(url);
  if (!res.ok) throw new Error(`OOSI API error: ${res.status}`);
  const json = await res.json();
  return Array.isArray(json) ? json : json?.data || [];
};

const useMalligyongDataLoader = () => {
  const [accessTableData, setAccessTableData] = useState({});
  const [probingTableData, setProbingTableData] = useState({});

  useEffect(() => {
    const fetchAccessTableData = async () => {
      try {
        let tmpData = {};
        const list = await fetchOosiData(PREFIX.access);
        for (const item of list) {
          const key = item.key || "";
          const cityName = key.split("/").pop().split(".")[0] || "";
          const lines = (item.file || "").split("\n");
          tmpData[cityName] = parseAccessData(lines, cityName);
        }
        setAccessTableData(removeEmptyData(tmpData));
      } catch (e) {
        console.error(e);
      }
    };

    const fetchProbingTableData = async () => {
      try {
        let tmpData = {};
        const list = await fetchOosiData(PREFIX.probing);
        for (const item of list) {
          const key = item.key || "";
          const fileName = key.split("/").pop().split(".")[0] || "";
          const [noradId, satName] = fileName.split("_");
          const lines = (item.file || "").split("\n");
          tmpData[satName] = parseProbingData(lines, noradId, satName);
        }
        setProbingTableData(removeEmptyData(tmpData));
      } catch (e) {
        console.error(e);
      }
    };

    fetchAccessTableData();
    fetchProbingTableData();
  }, []);
  return { accessTableData, probingTableData };
};

export default useMalligyongDataLoader;

const parseAccessData = (data, cityName) => {
  const tmpData = [];
  data.slice(0, -1).forEach(line => {
    const element = line.split(" ");
    const interval = element[1];
    const enteringDate = element[2];
    const enteringTime = element[3];
    const leavingDate = element[5];
    const leavingTime = element[6];
    const duration = element[8];

    const timeColor = determineColorBasedOnTime(enteringTime, leavingTime);
    const newFile = {
      [HEADER_KEYS.TYPE]: EVENTS.ACCESS,
      [HEADER_KEYS.NAME]: cityName,
      [HEADER_KEYS.CITY]: cityName,
      [HEADER_KEYS.INTERVAL]: interval,
      [HEADER_KEYS.ENTERING]: enteringDate + " " + enteringTime,
      [HEADER_KEYS.LEAVING]: leavingDate + " " + leavingTime,
      [HEADER_KEYS.ENTERING_LEAVING]: `${enteringDate} ${enteringTime} /\n ${leavingDate} ${leavingTime}`,
      [HEADER_KEYS.DURATION]: duration,
      color: timeColor
    };

    tmpData.push(newFile);
  });
  return tmpData;
};

const parseProbingData = (data, noradId, satName) => {
  const tmpData = [];
  data.slice(0, -1).forEach(line => {
    const element = line.split(" ");
    const interval = element[1];
    const enteringDate = element[2];
    const enteringTime = element[3].split("+")[0];
    const leavingDate = element[4];
    const leavingTime = element[5].split("+")[0];
    const duration = element[6];

    const newFile = {
      [HEADER_KEYS.TYPE]: EVENTS.PROBING,
      [HEADER_KEYS.NAME]: satName,

      [HEADER_KEYS.INTERVAL]: interval,
      [HEADER_KEYS.ENTERING]: enteringDate + " " + enteringTime,
      [HEADER_KEYS.LEAVING]: leavingDate + " " + leavingTime,
      [HEADER_KEYS.ENTERING_LEAVING]: `${enteringDate} ${enteringTime} /\n ${leavingDate} ${leavingTime}`,

      [HEADER_KEYS.DURATION]: duration,
      [HEADER_KEYS.SAT_NAME]: satName,
      [HEADER_KEYS.NORAD_ID]: noradId
    };

    tmpData.push(newFile);
  });

  return tmpData;
};

const removeEmptyData = data => {
  const tmpData = { ...data };
  Object.keys(tmpData).forEach(key => {
    if (tmpData[key].length === 0) {
      delete tmpData[key];
    }
  });
  return tmpData;
};
